// ── Co-watch engine ───────────────────────────────────────────────────────
// "Tonight, together" — find shows you AND friends are watching, and split them:
//   together  → you're on the EXACT same next episode → press play together now
//   nearMiss  → you both watch it but aren't in sync → who's ahead + by how much
// Multi-person by nature: a show in sync with three friends lists all three.
// Pure + unit-tested so the matching logic can't quietly drift (Jason 2026-06-29).
import { compareProgress, isCaughtUp, nextProgressAfter } from './episode.js'

// myProgress : { [showId]: {season, episode} }  — only your *watching* shows
// friendRows : [{ user_id, show_id, show_data, progress }] — friends' watching rows
// profiles   : { [userId]: {display_name, username, ...} }
export function computeCoWatch({ myProgress, friendRows, profiles } = {}) {
  const togetherByShow = {}
  const nearByShow = {}

  for (const r of friendRows || []) {
    const showId = r.show_id
    const mine = myProgress?.[showId]
    const prof = profiles?.[r.user_id]
    if (!mine || !r.progress || !prof) continue

    const cmp = compareProgress(mine, r.progress)   // 0 = same, >0 = you ahead, <0 = behind
    if (cmp === 0) {
      if (!togetherByShow[showId]) togetherByShow[showId] = { show: r.show_data, next: mine, friends: [] }
      togetherByShow[showId].friends.push(prof)
    } else {
      if (!nearByShow[showId]) nearByShow[showId] = { show: r.show_data, mine, friends: [] }
      nearByShow[showId].friends.push({ ...prof, theirProgress: r.progress, youAhead: cmp > 0 })
    }
  }

  const together = Object.values(togetherByShow)
    .filter(c => c.show && c.friends.length)
    .sort((a, b) => b.friends.length - a.friends.length)

  // Only surface a near-miss for a show that isn't already a perfect match.
  const nearMiss = Object.entries(nearByShow)
    .filter(([showId, c]) => c.show && c.friends.length && !togetherByShow[showId])
    .map(([, c]) => c)
    .sort((a, b) => b.friends.length - a.friends.length)

  return { together, nearMiss }
}

// Friendly gap for the catch-up nudge: same season → episode delta, else seasons.
export function describeGap(mine, theirs) {
  if (!mine || !theirs) return null
  const youAhead = compareProgress(mine, theirs) > 0
  const crossSeason = Number(mine.season) !== Number(theirs.season)
  let amount
  if (!crossSeason) {
    const d = Math.abs(Number(mine.episode) - Number(theirs.episode))
    amount = d <= 1 ? '1 episode' : `${d} episodes`
  } else {
    const d = Math.abs(Number(mine.season) - Number(theirs.season))
    amount = d <= 1 ? 'a season' : `${d} seasons`
  }
  // crossSeason gaps are unreliable: a finale-watcher's next-to-watch jumps to a
  // phantom S+1, so "a season ahead" can really mean "just caught up". Callers should
  // soften the wording when crossSeason is true and they can't confirm via air data.
  return { youAhead, amount, crossSeason }
}

// Decide what the "mark yours too" nudge should do, given the show (for its season
// structure), MY current next-to-watch, and the episode the pact-mate just watched.
// Pure so the one-tap co-watch sync (App's nextep:cowatch-mark handler) can't quietly
// drift. Returns exactly one of:
//   { action: 'navigate' }   — no local season data to compute "next"; open the show
//   { action: 'synced' }     — I'm already at/past that episode; nothing to mark
//   { action: 'mark', next } — advance my progress to `next` (one past the watched ep)
export function resolveCoWatchMark({ show, current, watched } = {}) {
  if (!show?.seasons || !watched) return { action: 'navigate' }
  const next = nextProgressAfter(watched, show.seasons)
  // Never regress: if my next-to-watch is already at or beyond `next`, I've seen it.
  if (current && compareProgress(current, next) >= 0) return { action: 'synced' }
  return { action: 'mark', next }
}

// Structured relative-progress for a co-watched show — the one source of truth the
// pact banner, Tonight-together near-miss, and Our Shows all read from. Pass lastAired
// (from effectiveLastAired) so an "ahead" that's really "just caught up" (phantom next
// season) resolves correctly. Returns null if either side's progress is missing.
export function coWatchStatus(mine, theirs, lastAired, opts = {}) {
  const iDone = !!opts.iDone, theyDone = !!opts.theyDone
  // Completion is AUTHORITATIVE over the raw progress number — someone can mark a show
  // complete (or finish it on a service you don't have) without their progress field
  // catching up. Jason 2026-06-30: Melissa finished Homestead on Hulu but her progress
  // lagged, so the comparison wrongly said "you're ahead".
  if (iDone && theyDone) return { inSync: true,  youAhead: false, caughtUp: true,  bothDone: true }
  if (theyDone)          return { inSync: false, youAhead: false, caughtUp: false, theyDone: true }
  if (iDone)             return { inSync: false, youAhead: true,  caughtUp: true,  iDone: true }
  if (!mine || !theirs) return null
  if (compareProgress(mine, theirs) === 0) return { inSync: true, youAhead: false, caughtUp: false }
  const g = describeGap(mine, theirs)
  const aheadProg = g.youAhead ? mine : theirs
  const caughtUp = !!(lastAired && isCaughtUp(aheadProg, lastAired))
  return { inSync: false, youAhead: g.youAhead, caughtUp, crossSeason: g.crossSeason, amount: g.amount }
}
